// Copyright (c) 2020-2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './FieldBuilder.scss';
import Button from "../../../../nor/ui/components/button/Button";
import Icon from "../../../../nor/ui/components/icon/Icon";
import {AddIcon, TrashIcon} from "../../../../assets/icon";
import TextField from "../../../../nor/ui/components/fields/text/TextField";
import FormFieldModel from "../../../../nor/ui/types/FormFieldModel";
import {ChangeCallback, VoidCallback} from "../../../../nor/ts/interfaces/callbacks";
import FormItemType from "../../../../nor/ui/types/FormItemType";
import {FormBuilderState} from "../form/FormBuilder";
import Popup from "../../../../nor/ui/components/popup/Popup";
import SelectFieldModel, {isSelectFieldModel, SelectFieldItem} from "../../../../nor/ui/types/items/SelectFieldModel";
import LogService from "../../../../nor/ts/LogService";
import {filter, map} from "../../../../nor/ts/modules/lodash";
import FormAppClassName from "../../../../constants/FormAppClassName";
import SliderFieldModel, { isSliderFieldModel } from "../../../../nor/ui/types/items/SliderFieldModel";
import IntegerField from "../../../../nor/ui/components/fields/integer/IntegerField";
import CheckboxField from "../../../../nor/ui/components/fields/checkbox/CheckboxField";

const LOG = LogService.createLogger('FieldBuilder');

export interface FieldBuilderState {

    actionsMenuOpen  : boolean;

}

export interface FieldBuilderProps {

    model     : FormFieldModel;
    onChange ?: ChangeCallback<FormFieldModel>;
    onDelete ?: VoidCallback;

}

export class FieldBuilder extends React.Component<FieldBuilderProps, FieldBuilderState> {

    private readonly _deleteModelCallback : VoidCallback;

    private readonly _changeLabelCallback : ChangeCallback<string|undefined>;
    private readonly _resetLabelCallback  : VoidCallback;
    private readonly _selectLabelCallback : VoidCallback;

    private readonly _changePlaceholderCallback : ChangeCallback<string|undefined>;
    private readonly _resetPlaceholderCallback  : VoidCallback;
    private readonly _deletePlaceholderCallback : VoidCallback;

    private readonly _changeMinValueCallback : ChangeCallback<any|undefined>;
    private readonly _resetMinValueCallback  : VoidCallback;
    private readonly _deleteMinValueCallback : VoidCallback;

    private readonly _changeMaxValueCallback : ChangeCallback<any|undefined>;
    private readonly _resetMaxValueCallback  : VoidCallback;
    private readonly _deleteMaxValueCallback : VoidCallback;

    private readonly _changeMinLengthCallback : ChangeCallback<any|undefined>;
    private readonly _resetMinLengthCallback  : VoidCallback;
    private readonly _deleteMinLengthCallback : VoidCallback;

    private readonly _changeMaxLengthCallback : ChangeCallback<any|undefined>;
    private readonly _resetMaxLengthCallback  : VoidCallback;
    private readonly _deleteMaxLengthCallback : VoidCallback;

    private readonly _changeRequiredCallback : ChangeCallback<boolean|undefined>;
    private readonly _resetRequiredCallback  : VoidCallback;
    private readonly _deleteRequiredCallback : VoidCallback;

    private readonly _changeKeyCallback : ChangeCallback<string|undefined>;
    private readonly _resetKeyCallback  : VoidCallback;
    private readonly _deleteKeyCallback : VoidCallback;

    private readonly _addItemCallback           : VoidCallback;
    private readonly _toggleActionsMenuCallback : VoidCallback;

    public constructor(props: FieldBuilderProps) {

        super(props);

        this.state = {
            actionsMenuOpen: false
        };

        this._deleteModelCallback = this._onDeleteModel.bind(this);

        this._changeLabelCallback = this._onChangeLabel.bind(this);
        this._resetLabelCallback  = this._onResetLabel.bind(this);
        this._selectLabelCallback = this._onDeleteLabel.bind(this);

        this._changePlaceholderCallback = this._onChangePlaceholder.bind(this);
        this._resetPlaceholderCallback  = this._onResetPlaceholder.bind(this);
        this._deletePlaceholderCallback = this._onDeletePlaceholder.bind(this);

        this._changeMinValueCallback = this._onChangeMinValue.bind(this);
        this._resetMinValueCallback  = this._onResetMinValue.bind(this);
        this._deleteMinValueCallback = this._onDeleteMinValue.bind(this);

        this._changeMaxValueCallback = this._onChangeMaxValue.bind(this);
        this._resetMaxValueCallback  = this._onResetMaxValue.bind(this);
        this._deleteMaxValueCallback = this._onDeleteMaxValue.bind(this);

        this._changeMinLengthCallback = this._onChangeMinLength.bind(this);
        this._resetMinLengthCallback  = this._onResetMinLength.bind(this);
        this._deleteMinLengthCallback = this._onDeleteMinLength.bind(this);

        this._changeMaxLengthCallback = this._onChangeMaxLength.bind(this);
        this._resetMaxLengthCallback  = this._onResetMaxLength.bind(this);
        this._deleteMaxLengthCallback = this._onDeleteMaxLength.bind(this);

        this._changeRequiredCallback = this._onChangeRequired.bind(this);
        this._resetRequiredCallback  = this._onResetRequired.bind(this);
        this._deleteRequiredCallback = this._onDeleteRequired.bind(this);

        this._changeKeyCallback = this._onChangeKey.bind(this);
        this._resetKeyCallback  = this._onResetKey.bind(this);
        this._deleteKeyCallback = this._onDeleteKey.bind(this);

        // For select fields
        this._addItemCallback         = this._onAddItem.bind(this);

        this._toggleActionsMenuCallback = this._onToggleActionsMenu.bind(this);

    }

    public render () {

        const item        : FormFieldModel      = this.props.model;
        const label       : string | undefined  = item?.label;
        const key         : string | undefined  = item?.key;
        const placeholder : string | undefined  = item?.placeholder;
        const minValue    : any | undefined     = item?.minValue;
        const maxValue    : any | undefined     = item?.maxValue;
        const minLength   : any | undefined     = item?.minLength;
        const maxLength   : any | undefined     = item?.maxLength;
        const required    : boolean | undefined = item?.required;

        const itemType = item?.type;

        const isIntegerField   = itemType === FormItemType.INTEGER_FIELD;
        const isCheckboxField  = itemType === FormItemType.CHECKBOX_FIELD;
        const isSelectField    = itemType === FormItemType.SELECT_FIELD;
        const isSliderField    = itemType === FormItemType.SLIDER_FIELD;
        const isSelectOrSlider = isSelectField || isSliderField;

        const canHaveRequired    = true;
        const canHaveItems       = isSelectOrSlider;
        const canHaveMinValue    = isIntegerField;
        const canHaveMaxValue    = isIntegerField;
        const canHaveMinLength   = !isIntegerField && !isSelectOrSlider;
        const canHaveMaxLength   = !isIntegerField && !isSelectOrSlider;
        const canHavePlaceholder = !isCheckboxField;

        return (
            <div className={FormAppClassName.FIELD_BUILDER}>

                <header>

                    <h4>Field ({item.type})</h4>

                    <Button
                        click={this._toggleActionsMenuCallback}
                        className={
                            FormAppClassName.FORM_BUILDER+ '-action-menu-button '
                            + FormAppClassName.FORM_BUILDER + (this.state.actionsMenuOpen ? '-action-menu-open' : '-action-menu-closed')
                        }
                    ><Icon><AddIcon /></Icon></Button>

                    <Popup open={this.state.actionsMenuOpen}>

                        {label !== undefined ? null : (
                            <Button click={this._resetLabelCallback}><Icon><AddIcon /></Icon> Label</Button>
                        )}

                        {key !== undefined ? null : (
                            <Button click={this._resetKeyCallback}><Icon><AddIcon /></Icon> Key</Button>
                        )}

                        { canHaveMinValue && minValue === undefined ? (
                            <Button click={this._resetMinValueCallback}><Icon><AddIcon /></Icon> Min value</Button>
                        ) : null}

                        { canHaveMaxValue && maxValue === undefined ? (
                            <Button click={this._resetMaxValueCallback}><Icon><AddIcon /></Icon> Max value</Button>
                        ) : null}

                        { canHaveMinLength && minLength === undefined ? (
                            <Button click={this._resetMinLengthCallback}><Icon><AddIcon /></Icon> Min length</Button>
                        ) : null}

                        { canHaveMaxLength && maxLength === undefined ? (
                            <Button click={this._resetMaxLengthCallback}><Icon><AddIcon /></Icon> Max length</Button>
                        ) : null}

                        { canHavePlaceholder && placeholder === undefined ? (
                            <Button click={this._resetPlaceholderCallback}><Icon><AddIcon /></Icon> Placeholder</Button>
                        ) : null}

                        { canHaveRequired && required === undefined ? (
                            <Button click={this._resetRequiredCallback}><Icon><AddIcon /></Icon> Required</Button>
                        ) : null}

                        { canHaveItems ? (
                            <Button click={this._addItemCallback}><Icon><AddIcon /></Icon> Item</Button>
                        ) : null}

                    </Popup>

                    <Button click={this._deleteModelCallback}><Icon><TrashIcon /></Icon></Button>

                </header>

                {label !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <TextField
                            label='Label'
                            placeholder={'Label'}
                            value={label}
                            change={this._changeLabelCallback}
                        >
                            <Button click={this._selectLabelCallback}><Icon><TrashIcon /></Icon></Button>
                        </TextField>
                    </div>
                ) : null}

                {key !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <TextField
                            label='Key'
                            placeholder={'Key'}
                            value={key}
                            change={this._changeKeyCallback}
                        >
                            <Button click={this._deleteKeyCallback}><Icon><TrashIcon /></Icon></Button>
                        </TextField>
                    </div>
                ) : null}

                { canHavePlaceholder && placeholder !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <TextField
                            label='Placeholder'
                            placeholder={'Placeholder'}
                            value={placeholder}
                            change={this._changePlaceholderCallback}
                        >
                            <Button click={this._deletePlaceholderCallback}><Icon><TrashIcon /></Icon></Button>
                        </TextField>
                    </div>
                ) : null}

                { canHaveMinValue && minValue !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <IntegerField
                            label='Min Value'
                            placeholder={'Min Value'}
                            value={minValue}
                            change={this._changeMinValueCallback}
                        >
                            <Button click={this._deleteMinValueCallback}><Icon><TrashIcon /></Icon></Button>
                        </IntegerField>
                    </div>
                ) : null}

                { canHaveMaxValue && maxValue !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <IntegerField
                            label='Max Value'
                            placeholder={'Max Value'}
                            value={maxValue}
                            change={this._changeMaxValueCallback}
                        >
                            <Button click={this._deleteMaxValueCallback}><Icon><TrashIcon /></Icon></Button>
                        </IntegerField>
                    </div>
                ) : null}

                { canHaveMinLength && minLength !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <IntegerField
                            label='Min Length'
                            placeholder={'Min Length'}
                            value={minLength}
                            change={this._changeMinLengthCallback}
                        >
                            <Button click={this._deleteMinLengthCallback}><Icon><TrashIcon /></Icon></Button>
                        </IntegerField>
                    </div>
                ) : null}

                { canHaveMaxLength && maxLength !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <IntegerField
                            label='Max Length'
                            placeholder={'Max Length'}
                            value={maxLength}
                            change={this._changeMaxLengthCallback}
                        >
                            <Button click={this._deleteMaxLengthCallback}><Icon><TrashIcon /></Icon></Button>
                        </IntegerField>
                    </div>
                ) : null}

                { canHaveRequired && required !== undefined ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        <CheckboxField
                            label='Field must have value'
                            value={required}
                            change={this._changeRequiredCallback}
                        >
                            <Button click={this._deleteRequiredCallback}><Icon><TrashIcon /></Icon></Button>
                        </CheckboxField>
                    </div>
                ) : null}

                { canHaveItems ? (
                    <div className={FormAppClassName.FIELD_BUILDER + '-content-page-row'}>
                        {map(((item as SelectFieldModel<any> | SliderFieldModel<any> )?.values ?? []), (selectItem : SelectFieldItem<any>, selectIndex : number) : any => {

                            const changeItemValueCallback = (value: string | undefined) => this._onChangeItemValue(selectIndex, value ?? '');
                            const changeItemLabelCallback = (value: string | undefined) => this._onChangeItemLabel(selectIndex, value ?? '');
                            const deleteItemCallback = () => this._onDeleteItem(selectIndex);

                            return (
                                <div
                                    key={`field-builder-select-item-${selectIndex}`}
                                    className={FormAppClassName.FIELD_BUILDER + '-select-item'}
                                >
                                    <TextField
                                        className={FormAppClassName.FIELD_BUILDER + '-select-item-label'}
                                        placeholder={'Label'}
                                        value={selectItem.label}
                                        change={changeItemLabelCallback}
                                    />
                                    <TextField
                                        className={FormAppClassName.FIELD_BUILDER + '-select-item-value'}
                                        placeholder={'Value'}
                                        value={selectItem.value}
                                        change={changeItemValueCallback}
                                    />
                                    <Button
                                        className={FormAppClassName.FIELD_BUILDER + '-select-item-delete-button'}
                                        click={deleteItemCallback}><Icon><TrashIcon /></Icon></Button>
                                </div>
                            );
                        })}
                    </div>
                ) : null}

            </div>
        );
    }


    private _changeModel (value : FormFieldModel) {

        if (this.props.onChange) {
            try {
                this.props.onChange(value);
            } catch (err) {
                console.error('Error while calling onChange prop: ', err);
            }
        } else {
            console.warn('Parent did not define onChange prop');
        }

    }

    private _onDeleteModel () {

        if (this.props.onDelete) {
            try {
                this.props.onDelete();
            } catch (err) {
                console.error('Error while calling onDelete prop: ', err);
            }
        } else {
            console.warn('Parent did not define onDelete prop');
        }

    }


    private _onChangeLabel (value: string | undefined) {

        this._changeModel({
            ...this.props.model,
            label: value ?? ''
        });

    }

    private _onResetLabel () {
        this._onChangeLabel('');
        this._closeActionsMenu();
    }

    private _onDeleteLabel () {

        const {label, ...newModel} = this.props.model;

        this._changeModel(newModel);

    }


    private _onChangePlaceholder (value: string | undefined) {

        this._changeModel({
            ...this.props.model,
            placeholder: value ?? ''
        });

    }

    private _onResetPlaceholder () {
        this._onChangePlaceholder('');
        this._closeActionsMenu();
    }

    private _onDeletePlaceholder () {

        const {placeholder, ...newModel} = this.props.model;

        this._changeModel(newModel);

    }


    private _onChangeMinValue (value: number | undefined) {
        this._changeModel({
            ...this.props.model,
            minValue: value ?? 0
        });
    }

    private _onResetMinValue () {
        this._onChangeMinValue(0);
        this._closeActionsMenu();
    }

    private _onDeleteMinValue () {
        const {minValue, ...newModel} = this.props.model;
        this._changeModel(newModel);
    }


    private _onChangeMaxValue (value: number | undefined) {
        this._changeModel({
            ...this.props.model,
            maxValue: value ?? 0
        });
    }

    private _onResetMaxValue () {
        this._onChangeMaxValue(0);
        this._closeActionsMenu();
    }

    private _onDeleteMaxValue () {
        const {maxValue, ...newModel} = this.props.model;
        this._changeModel(newModel);
    }


    private _onChangeMinLength (value: number | undefined) {
        this._changeModel({
            ...this.props.model,
            minLength: value ?? 0
        });
    }

    private _onResetMinLength () {
        this._onChangeMinLength(0);
        this._closeActionsMenu();
    }

    private _onDeleteMinLength () {
        const {minLength, ...newModel} = this.props.model;
        this._changeModel(newModel);
    }


    private _onChangeMaxLength (value: number | undefined) {
        this._changeModel({
            ...this.props.model,
            maxLength: value ?? 0
        });
    }

    private _onResetMaxLength () {
        this._onChangeMaxLength(0);
        this._closeActionsMenu();
    }

    private _onDeleteMaxLength () {
        const {maxLength, ...newModel} = this.props.model;
        this._changeModel(newModel);
    }



    private _onChangeRequired (value: boolean | undefined) {
        LOG.debug(`_onChangeRequired: value=`, value);
        this._changeModel({
            ...this.props.model,
            required: value ?? false
        });
    }

    private _onResetRequired () {
        this._onChangeRequired(true);
        this._closeActionsMenu();
    }

    private _onDeleteRequired () {
        const {required, ...newModel} = this.props.model;
        this._changeModel(newModel);
    }



    private _onChangeKey (value: string | undefined) {

        this._changeModel({
            ...this.props.model,
            key: value ?? ''
        });

    }

    private _onResetKey () {
        this._onChangeKey('');
        this._closeActionsMenu();
    }

    private _onDeleteKey () {

        const {key, ...newModel} = this.props.model;
        this._changeModel(newModel);

    }


    private _onAddItem () {

        const item = this.props.model;

        if ( isSelectFieldModel(item) || isSliderFieldModel(item) ) {

            const prevValues : SelectFieldItem<any>[] = item.values;

            const newValues : SelectFieldItem<any>[] = [...prevValues];

            newValues.push({
                label: '',
                value: ''
            });

            const newModel : SelectFieldModel<any> | SliderFieldModel<any> = {
                ...item,
                values: newValues
            };

            this._changeModel(newModel);

            this._closeActionsMenu();

        } else {
            LOG.error('_onAddItem: Model was not a SelectFieldModel or SliderFieldModel: ', item);
        }

    }

    private _onChangeItemValue (index: number, value: string) {

        const model = this.props.model;
        if ( isSelectFieldModel(model) || isSliderFieldModel(model) ) {

            const prevValues : SelectFieldItem<any>[] = model.values;

            const newValues : SelectFieldItem<any>[] = [...prevValues];

            if (index < newValues.length) {

                newValues[index] = {
                    ...prevValues[index],
                    value: value
                };

                const newModel : SelectFieldModel<any> | SliderFieldModel<any> = {
                    ...model,
                    values: newValues
                };

                this._changeModel(newModel);

            } else {
                LOG.error('_onChangeItemValue: Could not find item: ', index);
            }

        } else {
            LOG.error('_onChangeItemValue: Model was not a SelectFieldModel or SliderFieldModel: ', model);
        }

    }

    private _onChangeItemLabel (index: number, value: string) {

        const model = this.props.model;
        if ( isSelectFieldModel(model) || isSliderFieldModel(model) ) {

            const prevValues : SelectFieldItem<any>[] = model.values;

            const newValues : SelectFieldItem<any>[] = [...prevValues];

            if (index < newValues.length) {

                newValues[index] = {
                    ...prevValues[index],
                    label: value
                };

                const newModel : SelectFieldModel<any> | SliderFieldModel<any> = {
                    ...model,
                    values: newValues
                };

                this._changeModel(newModel);

            } else {
                LOG.error('_onChangeItemLabel: Could not find item: ', index);
            }

        } else {
            LOG.error('_onChangeItemLabel: Model was not a SelectFieldModel or SliderFieldModel: ', model);
        }

    }

    private _onDeleteItem (index: number) {

        const model = this.props.model;
        if ( isSelectFieldModel(model) || isSliderFieldModel(model) ) {

            const prevValues : SelectFieldItem<any>[] = model.values;

            if (index < prevValues.length) {

                const prevItem = prevValues[index];

                const newValues : SelectFieldItem<any>[] = filter(prevValues, (item : SelectFieldItem<any>) => item !== prevItem);

                const newModel : SelectFieldModel<any> | SliderFieldModel<any> = {
                    ...model,
                    values: newValues
                };

                this._changeModel(newModel);

            } else {
                LOG.error('_onDeleteItem: Could not find item: ', index);
            }

        } else {
            LOG.error('_onDeleteItem: Model was not a SelectFieldModel or SliderFieldModel: ', model);
        }

    }

    private _onToggleActionsMenu () {

        this.setState((state : FormBuilderState) => ({actionsMenuOpen: !state.actionsMenuOpen}));

    }

    private _closeActionsMenu () {

        this.setState({actionsMenuOpen: false});

    }

}

export default FieldBuilder;
